import React, {ComponentProps, useContext, useEffect, useImperativeHandle, useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {Observable} from 'rxjs';

import ReactFlow, {Background, Controls, Elements, ReactFlowProvider} from 'react-flow-renderer';
import {Button, Icon, Loader, Typography, useTheme} from '@innowise-group/ui-kit';
import {AttendanceStatistics, Can, Employee, EmployeeWorkspace, RequestOnPlan} from '@innowise-group/core';
import {subject} from '@casl/ability';

import * as Styled from './floor-plan.styles';
import {BookingWorkspaceModeFormValues} from '../booking-workspace-mode-form';
import {FloorData} from '../../pages/floor-details/use-plan-details.hook';
import {FloorsEventEmitterService} from '../../services/floors-event-emitter';
import {PlanElementView, PlanElementData} from '../plan-element-view';
import {SearchFloorValues} from '../search-floor-form';
import {WorkspaceElementData, WorkspaceView} from '../workspace-view';
import {useFloorDetails} from '../../pages/floor-details/use-floor-details.hook';
import {useRoomPolygonSelection} from '../../pages/floor-details/use-room-polygon-selection.hook';
import {ScreenZoomContext} from '@shared/core/services/screen-zoom';
import {endOfDay} from 'date-fns';

const nodeTypes = {
  rooms: PlanElementView,
  workspaces: WorkspaceView,
  stairs: PlanElementView,
  balconies: PlanElementView,
  lifts: PlanElementView,
  doors: PlanElementView,
};

type FloorMapProps = ComponentProps<typeof ReactFlow> & {
  Map: React.FC<ComponentProps<typeof ReactFlow>>;
  roomElements: Elements<PlanElementData>;
  roomsSelectable: boolean;
  reactFlowWrapper: React.MutableRefObject<HTMLDivElement | null>;
};

/** Map shell under ReactFlowProvider so room clicks can use store/selection APIs. */
const FloorMapWithPolygonSelection: React.FC<FloorMapProps> = ({
  Map,
  roomElements,
  roomsSelectable,
  reactFlowWrapper,
  ...mapProps
}) => {
  const {onElementClick, onPaneClick} = useRoomPolygonSelection(roomElements, reactFlowWrapper, roomsSelectable);

  return <Map {...mapProps} onElementClick={onElementClick} onPaneClick={onPaneClick} />;
};

export interface FloorPlanHandler {
  handleAddNewWorkspace: (isVirtual: boolean, rotateDegree?: number) => void;
  handleUploadPlan: () => void;
  handleSearchFloorSubmit: (data: SearchFloorValues) => void;
  handleBookingWorkspaceMode: (data: BookingWorkspaceModeFormValues) => void;
  handleBookWorkspace: () => void;
  abortChanges: () => Observable<void>;
  canSaveChanges: () => boolean;
  saveChanges: () => Observable<boolean>;
  downloadCSV: () => void;
  downloadPDF: () => void;
  downloadXLSX: () => void;
  handleSecondPlanFloorChange: () => void;
}
interface FloorPlanProps {
  floorId: string;
  requestData?: RequestOnPlan;
  isEditMode: boolean;
  setEmployees: React.Dispatch<React.SetStateAction<Employee[]>>;
  setFloorData?: React.Dispatch<React.SetStateAction<FloorData>>;
  setIsPlanExists?: React.Dispatch<React.SetStateAction<boolean>>;
  isShowSecondPlan: boolean;
  onFocusClick: (floor: FloorData) => void;
  onLoadingStateChanged?: (state: boolean) => void;
  onAttendanceLoadingStateChanged?: (state: boolean) => void;
  onMutationPendingChanged?: (state: boolean) => void;
  onDraftStateChanged?: (hasDraft: boolean) => void;
  focus: boolean;
  showNotifications: boolean;
  focusWorkspace?: Pick<EmployeeWorkspace, 'id' | 'floorId'>;
  Map: React.FC<ComponentProps<typeof ReactFlow>>;
  selectedBookingDetails?: BookingWorkspaceModeFormValues;
  attendanceStatistics?: AttendanceStatistics;
  setAttendanceStatistics?: React.Dispatch<React.SetStateAction<AttendanceStatistics>>;
}

export const FloorContext = React.createContext<{
  floorsEventEmitter: FloorsEventEmitterService;
  floor: FloorData;
  attendanceStatistics?: AttendanceStatistics;
  beginWorkspaceMutation: () => void;
  endWorkspaceMutation: () => void;
  handleAddNewWorkspace: (isVirtual: boolean, rotateDegree?: number) => void;
  workspaceElements: Elements<WorkspaceElementData>;
  roomElements: Elements<PlanElementData>;
  bookingFilterRange?: {start: Date; end: Date} | null;
}>(null);
export const ShowOnPlanRequest = React.createContext<
  RequestOnPlan & {focusWorkspace?: Pick<EmployeeWorkspace, 'id' | 'floorId'>}
>(null);

const FloorPlan = React.forwardRef<FloorPlanHandler, FloorPlanProps>(
  (
    {
      floorId,
      isEditMode,
      requestData,
      setEmployees,
      setFloorData,
      isShowSecondPlan = false,
      onFocusClick,
      onLoadingStateChanged,
      onAttendanceLoadingStateChanged,
      onMutationPendingChanged,
      onDraftStateChanged,
      focus,
      showNotifications,
      setIsPlanExists,
      focusWorkspace,
      Map,
      selectedBookingDetails,
      attendanceStatistics,
      setAttendanceStatistics,
    },
    ref,
  ) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const {screenZoom} = useContext(ScreenZoomContext);

    const resolvedFloorId = useMemo(() => {
      if (focusWorkspace?.floorId) {
        return focusWorkspace.floorId;
      }

      if (requestData?.idFloor) {
        return requestData.idFloor;
      }

      return floorId;
    }, [focusWorkspace?.floorId, requestData?.idFloor, floorId]);

    const {
      flowElements,
      workspaceElements,
      roomElements,
      floor,
      handleAddNewWorkspace,
      isPlanLoading,
      isAttendanceLoading,
      isPlanExists,
      reactFlowWrapper,
      onLoad,
      onDrop,
      onDragOver,
      handleUploadPlan,
      onNodeDragStart,
      onNodeDragStop,
      handleSearchFloorSubmit,
      handleBookingWorkspaceMode,
      handleBookWorkspace,
      abortChanges,
      canSaveChanges,
      saveChanges,
      selectable,
      handleSecondPlanFloorChange,
      floorsEventEmitter,
      isWorkspaceMutationPending,
      beginWorkspaceMutation,
      endWorkspaceMutation,
      hasDraftWorkspace,
    } = useFloorDetails(resolvedFloorId, !isEditMode, setEmployees, showNotifications, setAttendanceStatistics);
    const loadedFloorId = floor?.floor;

    useEffect(() => {
      if (typeof onLoadingStateChanged === 'function') {
        onLoadingStateChanged(isPlanLoading);
      }
    }, [isPlanLoading, onLoadingStateChanged]);

    useEffect(() => {
      if (typeof onAttendanceLoadingStateChanged === 'function') {
        onAttendanceLoadingStateChanged(isAttendanceLoading);
      }
    }, [isAttendanceLoading, onAttendanceLoadingStateChanged]);

    useEffect(() => {
      if (typeof onMutationPendingChanged === 'function') {
        onMutationPendingChanged(isWorkspaceMutationPending);
      }
    }, [isWorkspaceMutationPending, onMutationPendingChanged]);

    useEffect(() => {
      if (typeof onDraftStateChanged === 'function') {
        onDraftStateChanged(hasDraftWorkspace);
      }
    }, [hasDraftWorkspace, onDraftStateChanged]);

    useEffect(() => {
      if (floor?.floor && setFloorData) {
        setFloorData(floor);
      }
    }, [floor, setFloorData]);

    // Apply booking visualization only when booking details or floor identity change —
    // not when handleBookingWorkspaceMode identity churns (avoids extra refresh storms).
    const handleBookingWorkspaceModeRef = useRef(handleBookingWorkspaceMode);
    handleBookingWorkspaceModeRef.current = handleBookingWorkspaceMode;

    useEffect(() => {
      if (loadedFloorId === floorId && selectedBookingDetails) {
        const timeoutId = setTimeout(() => {
          handleBookingWorkspaceModeRef.current(selectedBookingDetails);
        }, 0);
        return () => clearTimeout(timeoutId);
      }
    }, [floorId, loadedFloorId, selectedBookingDetails]);

    useEffect(() => {
      if (setIsPlanExists) {
        setIsPlanExists(isPlanExists);
      }
    }, [setIsPlanExists, isPlanExists]);

    const bookingFilterRange = useMemo(() => {
      if (!selectedBookingDetails?.bookingMode || !selectedBookingDetails.startDay || !selectedBookingDetails.endDay) {
        return null;
      }
      return {
        start: new Date(selectedBookingDetails.startDay),
        end: endOfDay(new Date(selectedBookingDetails.endDay)),
      };
    }, [selectedBookingDetails]);

    useImperativeHandle(
      ref,
      () => ({
        handleAddNewWorkspace,
        handleUploadPlan,
        handleSearchFloorSubmit,
        handleSecondPlanFloorChange,
        handleBookingWorkspaceMode,
        handleBookWorkspace,
        abortChanges,
        canSaveChanges,
        saveChanges,
        downloadCSV: () => floorsEventEmitter.emit('downloadCSVReport'),
        downloadPDF: () => floorsEventEmitter.emit('downloadPDFReport'),
        downloadXLSX: () => floorsEventEmitter.emit('downloadXLSXReport'),
      }),
      [
        abortChanges,
        floorsEventEmitter,
        handleAddNewWorkspace,
        handleBookWorkspace,
        handleBookingWorkspaceMode,
        handleSearchFloorSubmit,
        handleSecondPlanFloorChange,
        handleUploadPlan,
        canSaveChanges,
        saveChanges,
      ],
    );

    const onMouseLeaveHandler = () => {
      if (isShowSecondPlan) {
        floorsEventEmitter.emit('refreshWorkspaces');
      }
    };

    const onFloorPlanClickHandler = () => {
      onFocusClick(floor);
    };

    if (isPlanLoading) {
      return (
        <Styled.FloorPlanContainer>
          <Styled.Overlay>
            <Loader />
          </Styled.Overlay>
        </Styled.FloorPlanContainer>
      );
    }

    return (
      <FloorContext.Provider
        value={{
          floorsEventEmitter,
          floor,
          attendanceStatistics,
          beginWorkspaceMutation,
          endWorkspaceMutation,
          handleAddNewWorkspace,
          workspaceElements,
          roomElements,
          bookingFilterRange,
        }}
      >
        <ShowOnPlanRequest.Provider value={{...requestData, focusWorkspace}}>
          <Styled.FloorPlanContainer onClick={onFloorPlanClickHandler} onMouseLeave={onMouseLeaveHandler}>
            {isPlanExists && !isPlanLoading ? (
              <ReactFlowProvider>
                {isShowSecondPlan && <Styled.Address type="h3">{floor.fullAddress}</Styled.Address>}
                <Styled.ReactFlowWrapper
                  ref={reactFlowWrapper}
                  selectable={selectable}
                  focus={isShowSecondPlan && focus}
                  screenZoom={screenZoom}
                >
                  <FloorMapWithPolygonSelection
                    Map={Map}
                    elements={flowElements}
                    nodeTypes={nodeTypes}
                    selectNodesOnDrag={false}
                    nodesConnectable={false}
                    onDragOver={onDragOver}
                    onDrop={onDrop}
                    onLoad={onLoad}
                    onNodeDragStart={onNodeDragStart}
                    onNodeDragStop={onNodeDragStop}
                    maxZoom={2}
                    minZoom={0.1}
                    roomElements={roomElements}
                    roomsSelectable={selectable}
                    reactFlowWrapper={reactFlowWrapper}
                  >
                    <Background color={theme.palette.general.darkGrey[100]} gap={16} />
                    <Controls showInteractive={false} />
                  </FloorMapWithPolygonSelection>
                </Styled.ReactFlowWrapper>
              </ReactFlowProvider>
            ) : (
              !isPlanExists &&
              !isPlanLoading && (
                <Styled.Overlay>
                  <Icon type="architectural_plan" size={100} />
                  <Typography>{t('pages.floors.noPlan')}</Typography>
                  <Can I="CREATE-PLAN" this={subject('FLOOR', {id: floorId})}>
                    <Button onClick={handleUploadPlan} icon="u_arrow-up">
                      {t('buttons.uploadPlan')}
                    </Button>
                  </Can>
                </Styled.Overlay>
              )
            )}
            {isWorkspaceMutationPending && (
              <Styled.MutationOverlay>
                <Loader size={48} />
              </Styled.MutationOverlay>
            )}
          </Styled.FloorPlanContainer>
        </ShowOnPlanRequest.Provider>
      </FloorContext.Provider>
    );
  },
);

export default React.memo(FloorPlan);
